/**
 * Schema do banco `BF_SELLER`: para cada tipo de dataset, em qual store ele entra, com que chave
 * e em que formato o payload baixado é gravado.
 *
 * **Um arquivo, um registro.** A chave é o caminho da URL do arquivo, que já segue um padrão:
 *
 *   .../585/115/catalogo_0.json        → [585, 115, 'catalogo_0.json']
 *   .../585/cliente/47551/boletos.json → [585, 'cliente', 47551, 'boletos.json']
 *
 * Isso resolve três coisas de uma vez: gravar um arquivo é um `put` só — o que derrubava os
 * aparelhos fracos era o número de requisições, não o volume de bytes; dois arquivos nunca se
 * sobrescrevem, porque caminhos diferentes são chaves diferentes, inclusive quando o descritor
 * vem sem `COD_SEGMENTO` e todos cairiam no mesmo escopo; e a chave fica legível no DevTools, o
 * que a URL inteira repetida em cada linha não era.
 *
 * O preço da escolha é que ler "um produto" passa a significar abrir o arquivo do catálogo
 * inteiro — por isso cada store é aberta uma vez por sessão e fica indexada em memória
 * (`bfSellerCache`). Quem invalida esse índice é o sync.
 *
 * O valor gravado é o payload já normalizado: o que o app refazia a cada leitura (campos que
 * chegam como string JSON, restrição agrupada por cliente, preço indexado por produto) acontece
 * aqui, uma vez, na gravação.
 */

export const BF_SELLER_STORES = [
  'catalogo',
  'preco',
  'estoque',
  'cliente',
  'categoria',
  'marcas',
  'saldo_flex',
  'janela_entrega',
  'pagebuilder',
  'indicadores',
  'indicador_loja',
  'campanha_indicador',
  'campanha_faixa',
  'campanha_mix_fornecedor',
  'campanha_cliente',
  'campanha_produto',
  'parametros_aws',
  'parametro_preco_base',
  'percentual_fornecedor',
  'cod_regime_tributario',
  'ciclo_venda_cliente_cliente',
  'recomendacaoBigData',
  'restricaoProdutoCliente',
  'precoPromocaoCliente',
  'ultima_compra',
  'cliente_boletos',
  'cliente_cortes',
  'combo',
] as const;

export type BfSellerStore = (typeof BF_SELLER_STORES)[number];

export type KeyPart = number | string;

export type DatasetScope = {
  loja: number;
  segmento: number;
};

/** Contexto do arquivo em processamento. */
export type DatasetRowContext = {
  /** URL de origem — é dela que sai a chave do registro. */
  url: string;
};

export type DatasetSpec = {
  store: BfSellerStore;
  /**
   * Payload baixado → valor gravado. `null` significa "nada a gravar": arquivo vazio não pode
   * apagar o que já existe.
   */
  toValue: (payload: any, ctx: DatasetRowContext) => unknown | null;
};

/** Chave de um arquivo cuja URL não deu para interpretar. */
export const SINGLETON_ID = '@';

/** Tipos de dataset que continuam no banco de imagens (`BF_SELLER_IMAGES`) e não entram aqui. */
const IMAGE_DATASET_TYPES = new Set(['imagens', 'imagens_zip']);

export const isImageDatasetType = (tipo: string): boolean => IMAGE_DATASET_TYPES.has(String(tipo || ''));

// -------------------------------------------------------------------------------------------
// chave do arquivo
// -------------------------------------------------------------------------------------------

const SO_DIGITOS = /^\d+$/;

/**
 * Caminho da URL → chave do registro.
 *
 * Host e pastas fixas antes da loja são descartados: são iguais em todo arquivo e só inchariam a
 * chave. A leitura começa no primeiro trecho numérico, que é a loja. Trecho numérico vira número,
 * para a chave ordenar como número e não como texto.
 *
 * Sem trecho numérico, fica o caminho inteiro; sem caminho, o singleton — nunca uma chave vazia,
 * que o IndexedDB rejeita.
 */
export const fileKeyFromUrl = (url: string): KeyPart[] => {
  const semQuery = String(url || '').split(/[?#]/)[0];
  const semHost = semQuery.replace(/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\/[^/]*/, '');

  const partes = semHost
    .split('/')
    .filter(Boolean)
    .map((parte) => {
      try {
        return decodeURIComponent(parte);
      } catch {
        return parte;
      }
    });

  if (!partes.length) return [SINGLETON_ID];

  const inicio = partes.findIndex((parte) => SO_DIGITOS.test(parte));
  const identidade = inicio >= 0 ? partes.slice(inicio) : partes;

  return identidade.map((parte) => (SO_DIGITOS.test(parte) ? Number(parte) : parte));
};

// -------------------------------------------------------------------------------------------
// helpers de normalização
// -------------------------------------------------------------------------------------------

const isPlainObject = (value: any): value is Record<string, any> =>
  value != null && typeof value === 'object' && !Array.isArray(value);

const asArray = (value: any): any[] => (Array.isArray(value) ? value : []);

const num = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/** `0` para loja/segmento ausentes. */
export const toScopePart = (value: unknown): number => num(value) ?? 0;

/** Loja e segmento do descritor. Não entram na chave; ficam no `__meta` para diagnóstico. */
export const resolveDatasetScope = (dataset: {
  COD_LOJA?: number | string | null;
  COD_SEGMENTO?: number | string | null;
}): DatasetScope => ({
  loja: toScopePart(dataset?.COD_LOJA),
  segmento: toScopePart(dataset?.COD_SEGMENTO),
});

/** Alguns payloads chegam embrulhados em `{ data: ... }` (às vezes duas vezes). */
const unwrapData = (payload: any): any => {
  let current = payload;
  for (let depth = 0; depth < 2; depth += 1) {
    if (isPlainObject(current) && 'data' in current && Object.keys(current).length <= 3) {
      current = current.data;
      continue;
    }
    break;
  }
  return current;
};

const parseIfJsonString = <T>(value: unknown, fallback: T): T => {
  if (typeof value !== 'string') return (value ?? fallback) as T;
  try {
    return JSON.parse(value || 'null') ?? fallback;
  } catch {
    return fallback;
  }
};

const vazio = (value: any): boolean =>
  value == null || (Array.isArray(value) && !value.length) || (isPlainObject(value) && !Object.keys(value).length);

/** O arquivo como veio, sem embrulho. É o caso da maioria: lista que o app lê inteira. */
const asIs = (payload: any): unknown | null => {
  const value = unwrapData(payload);
  return vazio(value) ? null : value;
};

/** Agrupa a lista do arquivo num mapa `{ [chave]: valor }`. */
const indexBy = <T>(
  payload: any,
  keyOf: (item: any) => number | null,
  reduce: (bucket: T | undefined, item: any) => T
): Record<string, T> | null => {
  const out: Record<string, T> = {};
  let algum = false;

  for (const item of asArray(unwrapData(payload))) {
    const key = keyOf(item);
    if (key == null) continue;
    out[key] = reduce(out[key], item);
    algum = true;
  }

  return algum ? out : null;
};

// -------------------------------------------------------------------------------------------
// normalizações específicas
// -------------------------------------------------------------------------------------------

/** Campos que chegam como string JSON são desserializados na gravação, não a cada leitura. */
const clienteToValue = (payload: any): unknown | null => {
  const linhas = asArray(unwrapData(payload));
  if (!linhas.length) return null;

  return linhas.map((cliente) => ({
    ...cliente,
    COD_SEGMENTO: parseIfJsonString(cliente?.COD_SEGMENTO, [] as any[]),
    DT_ULTIMA_COMPRA: parseIfJsonString(cliente?.DT_ULTIMA_COMPRA, [] as any[]),
  }));
};

/**
 * Preço aceita dois formatos:
 *  - novo: `{ [COD_LOJA]: { [NRO_SEGMENTO]: { [COD_PRODUTO]: { [QTD_EMBALAGEM]: item } } } }`
 *  - legado: `{ PRODUTOS: { [COD_PRODUTO]: { [QTD_EMBALAGEM]: item } }, FILTRO: { COD_LOJA, NRO_SEGMENTO } }`
 *
 * Os dois viram `{ [produto]: { [loja]: { [segmento]: { [qtd]: item } } } }`: quem lê preço parte
 * sempre do produto — a lista precifica a página visível, o carrinho precifica os itens — e é o
 * `handlePrices` que escolhe loja e segmento depois.
 */
const precoToValue = (payload: any): unknown | null => {
  const src = unwrapData(payload);
  if (!isPlainObject(src)) return null;

  const porProduto: Record<string, Record<string, Record<string, any>>> = {};
  let algum = false;

  const guardar = (produto: number, loja: number, segmento: number, embalagens: any) => {
    if (!porProduto[produto]) porProduto[produto] = {};
    if (!porProduto[produto][loja]) porProduto[produto][loja] = {};
    porProduto[produto][loja][segmento] = embalagens;
    algum = true;
  };

  if (isPlainObject(src.PRODUTOS)) {
    const loja = toScopePart(src?.FILTRO?.COD_LOJA);
    const segmento = toScopePart(src?.FILTRO?.NRO_SEGMENTO);
    for (const [codProduto, embalagens] of Object.entries(src.PRODUTOS)) {
      const produto = num(codProduto);
      if (produto == null || !isPlainObject(embalagens)) continue;
      guardar(produto, loja, segmento, embalagens);
    }
  } else {
    for (const [lojaKey, porSegmento] of Object.entries(src)) {
      const loja = num(lojaKey);
      if (loja == null || !isPlainObject(porSegmento)) continue;
      for (const [segmentoKey, produtos] of Object.entries(porSegmento)) {
        const segmento = num(segmentoKey);
        if (segmento == null || !isPlainObject(produtos)) continue;
        for (const [codProduto, embalagens] of Object.entries(produtos)) {
          const produto = num(codProduto);
          if (produto == null || embalagens == null) continue;
          guardar(produto, loja, segmento, embalagens);
        }
      }
    }
  }

  return algum ? porProduto : null;
};

/** Última compra é sempre lida por cliente: `{ [pessoa]: { [produto]: linha } }`. */
const ultimaCompraToValue = (payload: any): unknown | null => {
  const out: Record<string, Record<string, any>> = {};
  let algum = false;

  for (const item of asArray(unwrapData(payload))) {
    const pessoa = num(item?.SEQPESSOA);
    const produto = num(item?.SEQPRODUTO);
    if (pessoa == null || produto == null) continue;
    if (!out[pessoa]) out[pessoa] = {};
    out[pessoa][produto] = item;
    algum = true;
  }

  return algum ? out : null;
};

/** `{ [COD_PESSOA]: { VLR_ERP } }` — tolera wrapper e valor solto no lugar do objeto. */
const saldoFlexToValue = (payload: any): unknown | null => {
  const src = unwrapData(payload);
  if (!isPlainObject(src)) return null;

  const out: Record<string, { VLR_ERP: number | null }> = {};
  let algum = false;

  for (const [codPessoa, node] of Object.entries(src)) {
    const pessoa = num(codPessoa);
    if (pessoa == null) continue;
    const bruto = isPlainObject(node) ? ((node as any).VLR_ERP ?? node) : node;
    out[pessoa] = { VLR_ERP: num(bruto) };
    algum = true;
  }

  return algum ? out : null;
};

/**
 * 152 mil pares (cliente, produto) viram um mapa por cliente. A tela pergunta "o que este cliente
 * não pode comprar" a cada página aberta; sem o agrupamento seria varrer o array inteiro toda vez.
 */
const restricaoToValue = (payload: any): unknown | null =>
  indexBy<number[]>(
    payload,
    (item) => num(item?.COD_CLIENTE),
    (bucket, item) => {
      const produto = num(item?.COD_PRODUTO);
      const atual = bucket ?? [];
      if (produto != null) atual.push(produto);
      return atual;
    }
  );

/**
 * Array do dataset → árvore `[COD_CLIENTE][QTD_EMBALAGEM][COD_PRODUTO][COD_LOJA][NRO_SEGMENTO]`,
 * que é como o `handlePrices` procura o preço promocional.
 *
 * Repetição dentro do mesmo arquivo fica com o **menor** `VLR_PRECO` — mesma regra que o
 * download aplicava antes. Payload que já chega em árvore (formato antigo) passa direto.
 */
const precoPromocaoClienteToValue = (payload: any): unknown | null => {
  const src = unwrapData(payload);
  if (isPlainObject(src)) return vazio(src) ? null : src;

  const porCliente: Record<string, any> = {};
  let algum = false;

  for (const item of asArray(src)) {
    if (!isPlainObject(item)) continue;
    const cliente = num(item.COD_CLIENTE);
    const produto = num(item.COD_PRODUTO);
    if (cliente == null || produto == null) continue;

    if (!porCliente[cliente]) porCliente[cliente] = {};
    const porQtd = porCliente[cliente];
    if (!porQtd[item.QTD_EMBALAGEM]) porQtd[item.QTD_EMBALAGEM] = {};
    const porProduto = porQtd[item.QTD_EMBALAGEM];
    if (!porProduto[produto]) porProduto[produto] = {};
    const porLoja = porProduto[produto];
    if (!porLoja[item.COD_LOJA]) porLoja[item.COD_LOJA] = {};
    const porSegmento = porLoja[item.COD_LOJA];

    const atual = porSegmento[item.NRO_SEGMENTO];
    if (!atual || Number(item.VLR_PRECO) < Number(atual.VLR_PRECO)) porSegmento[item.NRO_SEGMENTO] = item;
    algum = true;
  }

  return algum ? porCliente : null;
};

/** Ciclo de venda agrupado por cliente e ordenado por semana, que é como o cadastro lê. */
const cicloVendaToValue = (payload: any): unknown | null => {
  const porCliente = indexBy<any[]>(
    payload,
    (item) => num(item?.COD_CLIENTE),
    (bucket, item) => [...(bucket ?? []), item]
  );
  if (!porCliente) return null;

  for (const linhas of Object.values(porCliente)) {
    linhas.sort((a, b) => Number(a?.SEMANA_CICLO_ID ?? 0) - Number(b?.SEMANA_CICLO_ID ?? 0));
  }
  return porCliente;
};

// -------------------------------------------------------------------------------------------
// mapa tipo de dataset → spec
// -------------------------------------------------------------------------------------------

export const DATASET_SPECS: Record<string, DatasetSpec> = {
  catalogo: { store: 'catalogo', toValue: asIs },

  preco: { store: 'preco', toValue: precoToValue },
  preco_novo: { store: 'preco', toValue: precoToValue },

  cliente: { store: 'cliente', toValue: clienteToValue },

  estoque: { store: 'estoque', toValue: asIs },
  categoria: { store: 'categoria', toValue: asIs },
  marcas: { store: 'marcas', toValue: asIs },
  janela_entrega: { store: 'janela_entrega', toValue: asIs },
  pagebuilder: { store: 'pagebuilder', toValue: asIs },
  indicadores: { store: 'indicadores', toValue: asIs },
  indicador_loja: { store: 'indicador_loja', toValue: asIs },
  campanha_indicador: { store: 'campanha_indicador', toValue: asIs },
  campanha_faixa: { store: 'campanha_faixa', toValue: asIs },
  campanha_mix_fornecedor: { store: 'campanha_mix_fornecedor', toValue: asIs },
  campanha_cliente: { store: 'campanha_cliente', toValue: asIs },
  campanhaClientes: { store: 'campanha_cliente', toValue: asIs },
  campanha_produto: { store: 'campanha_produto', toValue: asIs },
  parametros_aws: { store: 'parametros_aws', toValue: asIs },
  parametro_preco_base: { store: 'parametro_preco_base', toValue: asIs },
  percentual_fornecedor: { store: 'percentual_fornecedor', toValue: asIs },
  cod_regime_tributario: { store: 'cod_regime_tributario', toValue: asIs },
  substituicao_tributaria_aws: { store: 'cod_regime_tributario', toValue: asIs },
  recomendacaoBigData: { store: 'recomendacaoBigData', toValue: asIs },
  cliente_boletos: { store: 'cliente_boletos', toValue: asIs },
  cliente_cortes: { store: 'cliente_cortes', toValue: asIs },
  combo: { store: 'combo', toValue: asIs },

  saldo_flex: { store: 'saldo_flex', toValue: saldoFlexToValue },
  saldo_flex_novo: { store: 'saldo_flex', toValue: saldoFlexToValue },

  ciclo_venda_cliente_cliente: { store: 'ciclo_venda_cliente_cliente', toValue: cicloVendaToValue },
  ciclo_venda_cliente: { store: 'ciclo_venda_cliente_cliente', toValue: cicloVendaToValue },
  restricaoProdutoCliente: { store: 'restricaoProdutoCliente', toValue: restricaoToValue },
  precoPromocaoCliente: { store: 'precoPromocaoCliente', toValue: precoPromocaoClienteToValue },
  ultima_compra: { store: 'ultima_compra', toValue: ultimaCompraToValue },
};

export const getDatasetSpec = (tipo: string): DatasetSpec | null => DATASET_SPECS[String(tipo || '')] ?? null;

/** Quantos itens o valor carrega — para o log do sync e para saber se a store tem dado. */
export const countItems = (value: unknown): number => {
  if (Array.isArray(value)) return value.length;
  if (isPlainObject(value)) return Object.keys(value).length;
  return value == null ? 0 : 1;
};
